import React, { useState } from 'react'

import { ListItemControl } from '@interco/inter-toranja'
import { useActionHandler } from '@/hooks/useActionHandler'
import { useHomeCreditListItemTag } from '@/hooks/useHomeCreditListItemTag'

import * as S from './styles'

interface ListItemControlSDUIProps {
  items: ListItemControlData[]
  style?: { marginTop?: string; marginBottom?: string }
  componentAnalytics?: Analytics
}

const buildTrailingProps = (
  item: ListItemControlData,
  controlState: Record<string, boolean | number>,
  setControlState: React.Dispatch<React.SetStateAction<Record<string, boolean | number>>>,
  dispatchAction: (action: NavigationAction | BottomSheetAction, analytics?: Analytics) => void,
  componentAnalytics: Analytics | undefined,
) => {
  if (!item.trailingVariant || !item.trailingProps) return {}
  const tp = item.trailingProps

  if (item.trailingVariant === 'checkbox') {
    return {
      trailingVariant: 'checkbox' as const,
      trailingProps: {
        checked: (controlState[item.id] as boolean) ?? tp.checked,
        indeterminate: tp.indeterminate,
        onCheckboxChange: (checked: boolean) => {
          setControlState((prev) => ({ ...prev, [item.id]: checked }))
          dispatchAction(item.action, item.analytics ?? componentAnalytics)
        },
      },
    }
  }
  if (item.trailingVariant === 'switch') {
    return {
      trailingVariant: 'switch' as const,
      trailingProps: {
        checked: (controlState[item.id] as boolean) ?? tp.checked,
        onSwitchChange: (checked: boolean) => {
          setControlState((prev) => ({ ...prev, [item.id]: checked }))
          dispatchAction(item.action, item.analytics ?? componentAnalytics)
        },
      },
    }
  }
  if (item.trailingVariant === 'stepper') {
    return {
      trailingVariant: 'stepper' as const,
      trailingProps: {
        value: (controlState[item.id] as number) ?? tp.value ?? 0,
        min: tp.min ?? 0,
        max: tp.max ?? 100,
        step: tp.step ?? 1,
        onStepperChange: (value: number) => {
          setControlState((prev) => ({ ...prev, [item.id]: value }))
        },
      },
    }
  }
  if (item.trailingVariant === 'radio') {
    return {
      trailingVariant: 'radio' as const,
      trailingProps: {
        checked: tp.checked,
        value: tp.radioValue,
        onRadioChange: () => dispatchAction(item.action, item.analytics ?? componentAnalytics),
      },
    }
  }
  return {}
}

export const ListItemControlSDUI: React.FC<ListItemControlSDUIProps> = ({
  items,
  style,
  componentAnalytics,
}) => {
  const { dispatchAction } = useActionHandler()
  const fireListItemTag = useHomeCreditListItemTag()
  const [controlState, setControlState] = useState<Record<string, boolean | number>>(() => {
    const initial: Record<string, boolean | number> = {}
    items.forEach((item) => {
      if (item.trailingVariant === 'checkbox' || item.trailingVariant === 'switch') {
        initial[item.id] = item.trailingProps?.checked ?? false
      } else if (item.trailingVariant === 'stepper') {
        initial[item.id] = item.trailingProps?.value ?? item.trailingProps?.min ?? 0
      }
    })
    return initial
  })

  return (
    <S.ContainerListItemDefault style={style}>
      {items.map((item) => (
        <ListItemControl
          key={item.id}
          label={item.title}
          paragraph={item.description}
          showDivider={item.showDivider === 'enabled'}
          variant={(item.variant as 'inset') ?? 'inset'}
          leadingProps={
            item.showLeading !== 'disabled' && item.icon
              ? {
                  iconProps: {
                    asset: item.icon as never,
                    color: 'Icon/Neutral/Primary',
                    contentDescription: 'Icon',
                    size: 'medium',
                  },
                  type: 'icon',
                }
              : undefined
          }
          {...buildTrailingProps(
            item,
            controlState,
            setControlState,
            dispatchAction,
            componentAnalytics,
          )}
          onTag={(tag) => fireListItemTag(tag, item.title)}
          onClick={() => dispatchAction(item.action, item.analytics ?? componentAnalytics)}
        />
      ))}
    </S.ContainerListItemDefault>
  )
}
